import { FetchHttpClient, HttpClient, HttpClientError, HttpClientResponse } from "@effect/platform"
import { AdapterError, type DocumentAdapter, type ModelConfig } from "@supergrain/silo"
import { jsonApiProcessor } from "@supergrain/silo/processors/json-api"
import { Effect } from "effect"

import type { TypeToModel } from "@shared/models/type-to-model"
import ENV from "cc-frontend/config/environment"

type ModelType = keyof TypeToModel & string
type ModelConfigs = { [K in ModelType]: ModelConfig<TypeToModel> }

interface AjaxHeadersProvider {
  headers: Record<string, string>
}

type SubscribeToDocument = (type: ModelType, id: string) => void

const SUBSCRIBE_DELAY_MS = 1000

export function createSupergrainModelConfigs(
  ajax: AjaxHeadersProvider,
  subscribe: SubscribeToDocument
): ModelConfigs {
  function modelConfig(
    type: ModelType,
    urlForIds: (ids: readonly string[]) => string,
    transformResponse: (response: unknown) => unknown = identity,
    headers: HeadersInit = {}
  ): ModelConfig<TypeToModel> {
    return {
      adapter: jsonApiAdapter(type, ajax, subscribe, urlForIds, transformResponse, headers),
      processor: jsonApiProcessor,
    }
  }

  return {
    "action-summary": modelConfig("action-summary", endpoint("action_summaries")),
    "card-stack": modelConfig("card-stack", endpoint("card_stacks")),
    "card-stack-summary": modelConfig("card-stack-summary", endpoint("card_stack_summaries")),
    "card-stack-version": modelConfig("card-stack-version", endpoint("card_stack_versions")),
    "class-website": modelConfig("class-website", endpoint("class_websites")),
    "class-website-lesson": modelConfig("class-website-lesson", endpoint("class_website_lessons")),
    comment: modelConfig("comment", endpoint("comments")),
    deal: modelConfig("deal", endpoint("deals")),
    "fiscal-group": modelConfig("fiscal-group", endpoint("fiscal_groups")),
    "google-classroom-card-mediator": modelConfig(
      "google-classroom-card-mediator",
      endpoint("google_classroom_card_mediators")
    ),
    "google-drive-file": modelConfig("google-drive-file", endpoint("google_drive_files")),
    "group-summary": modelConfig("group-summary", endpoint("group_summaries")),
    "kb-entry": modelConfig("kb-entry", endpoint("kb")),
    "planbook-summary": modelConfig("planbook-summary", endpoint("planbook_summaries")),
    "prismic-doc": modelConfig("prismic-doc", endpoint("prismic-doc")),
    "rotation-calendar": modelConfig("rotation-calendar", endpoint("rotation_calendars")),
    "school-year": modelConfig("school-year", endpoint("school_years")),
    "standard-jurisdiction-index": modelConfig(
      "standard-jurisdiction-index",
      constantEndpoint("standard_jurisdictions")
    ),
    "standard-jurisdiction-index-admin": modelConfig(
      "standard-jurisdiction-index-admin",
      constantEndpoint("standard_jurisdictions?admin_view=true")
    ),
    "standard-jurisdiction": modelConfig(
      "standard-jurisdiction",
      endpoint("standard_jurisdictions")
    ),
    "standard-set-admin": modelConfig("standard-set-admin", endpoint("standard_sets_admin")),
    "standard-set": modelConfig("standard-set", endpoint("standard_sets")),
    "user-summary": modelConfig("user-summary", endpoint("user_summaries")),
    course: modelConfig("course", endpoint("courses")),
    group: modelConfig("group", endpoint("groups")),
    planbook: modelConfig("planbook", endpoint("planbooks")),
    trial: modelConfig("trial", endpoint("trials")),
    user: modelConfig("user", endpoint("users")),
  }
}

export function hasSupergrainModelConfig(configs: ModelConfigs, type: string): type is ModelType {
  return Object.prototype.hasOwnProperty.call(configs, type)
}

function jsonApiAdapter(
  type: ModelType,
  ajax: AjaxHeadersProvider,
  subscribe: SubscribeToDocument,
  urlForIds: (ids: readonly string[]) => string,
  transformResponse: (response: unknown) => unknown,
  headers: HeadersInit
): DocumentAdapter {
  return {
    // A find both fetches the current value and, once the load succeeds,
    // registers a live subscription for each id so the server streams us other
    // users' patches. `find` is re-invoked per retry attempt, so we subscribe
    // only on success (via `tap`) rather than for a load that never lands.
    find: (ids) =>
      requestJson(type, ajax, urlForIds(ids), ids, headers).pipe(
        Effect.map(transformResponse),
        Effect.tap(() =>
          Effect.sync(() => {
            setTimeout(() => {
              for (const id of ids) subscribe(type, id)
            }, SUBSCRIBE_DELAY_MS)
          })
        )
      ),
  }
}

function requestJson(
  type: ModelType,
  ajax: AjaxHeadersProvider,
  url: string,
  ids: readonly string[],
  headers: HeadersInit
): Effect.Effect<unknown, AdapterError> {
  return HttpClient.get(url, {
    acceptJson: true,
    headers: {
      ...ajax.headers,
      ...headers,
    },
  }).pipe(
    Effect.flatMap(HttpClientResponse.filterStatusOk),
    Effect.flatMap((response) => response.json),
    Effect.mapError((cause) => adapterErrorFromCause(type, ids, cause)),
    Effect.provide(FetchHttpClient.layer)
  )
}

function endpoint(path: string): (ids: readonly string[]) => string {
  return (ids) => `${ENV.API_ROOT}/${path}/${ids.join(",")}`
}

function constantEndpoint(path: string): () => string {
  return () => `${ENV.API_ROOT}/${path}`
}

function identity(response: unknown): unknown {
  return response
}

function adapterErrorFromCause(
  type: ModelType,
  ids: readonly string[],
  cause: unknown
): AdapterError {
  if (cause instanceof AdapterError) return cause

  return new AdapterError({
    type,
    keys: ids,
    cause,
    retryable: retryableHttpError(cause),
  })
}

function retryableHttpError(cause: unknown): boolean | undefined {
  if (!HttpClientError.isHttpClientError(cause)) return undefined
  if (cause._tag === "RequestError") return true

  const status = cause.response.status
  return status === 429 || status >= 500
}
